import { StyledProps, StyledPropsWithChildren } from '@front10';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import React, { useRef } from 'react';
import { getAirports } from '../../../_integrations/everymundo/airports';
//-------------------------------------
// Integration Layer Default Actions
//-------------------------------------
import { getFares } from '../../../_integrations/everymundo/fares';
import Error from '../../alerts/ContainerErrorAlert';
import NoResults from '../../alerts/NoFeaturedPopularFaresAlert';
import InlineFilters from '../../controls/InlineFilters/InlineFilters';
import Box from '../../elements/Box';
import Col from '../../elements/Col';
//-------------------------------------
// Basic components here
//-------------------------------------
import Row from '../../elements/Row';
import Disclaimer from '../../others/Disclaimer';
import Loading from '../../others/Loading';
import NoFaresBehavior from '../../others/NoFaresBehavior/NoFaresBehavior';
import Skeleton from '../../others/Skeleton/SkeletonFareTable';
import Pagination from '../../pagination/LoadMorePaginator';
import withCommonEMContainer from '../_commons/CommonEMContainer';
import { useFilters, OriginDestinationLogic } from '../_commons/filters';
import { useListFocusHandlers, useListItemFocus } from '../_commons/list';
import prePopulationDefaultProps from '../_commons/prepopulationDefaultProps/groupedRoutesAggregation';
import { useFareItemProps } from '../_commons/tracking';
import { FareTable } from './FareTable';
import { BoxProps } from '../../elements/Box/Box';
import HeaderContainer from '../../elements/HeaderContainer';
import SecondaryFilters from '../../controls/SecondaryFilters';

interface Props extends WithEMContainerProps {
  componentName: string;
  /**
   * Action to be executed when clicking in fareItem
   */
  fareItemOnClickAction: $TSFixMe;
  /**
   * Possible ways to get fares from API
   */
  faresTransformationLogic: 'grouped-routes' | 'aggregation';
  /**
   * Filters settings
   */
  filters: $TSFixMe;
  /**
   * Function to getFares
   */
  getFares: $TSFixMe;
  /**
   * Function for request to obtain airports
   */
  getAirports: $TSFixMe;
  /**
   * Component Initial Fares
   */
  initialFares: $TSFixMe[];
  /**
   * Component Behavior when there are not results available
   */
  noFaresBehavior: NoFaresBehavior;
  /**
   * Settings of the Pagination
   */
  paginationSettings: PaginationSettings;
  titleKey: string;
  titleSettings: StyledProps<$TSFixMe>;
  /**
   * Component PrePopulation
   */
  prePopulation: $TSFixMe;
  components?: ComponentsProps;
  showFilters?: boolean;
  /**
   * Login to load origin and destination airports
   * same: Origin and Destination are pre-populated or not
   *      a single airport service call is done to fill out the Origin
   *      and Destinations filters (for performance purposes).
   * different: Origin and Destination are pre-populated or not
   *      2 airports service calls are done to fill out the Origin
   *      and Destinations filters.
   */
  originDestinationLogic?: 'same' | 'different';
  /**
   * Show the SubTitle Component
   */
  showSubTitle: boolean;
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: $TSFixMe;
  /**
   * titleKey of the component
   */
  subTitleKey: string;
  /**
   * SubTitle Component
   */
  subTitleSettings: $TSFixMe;
  /**
   * Show Secondary Filters
   */
  showSecondaryFilters: boolean;
  /**
   * Secondary Filters settings
   */
  secondaryFilters: $TSFixMe;
  /**
   * Enable SEO in the component
   */
  enableSEO?: Boolean;
}

interface ComponentsProps {
  skeleton?: React.ComponentPropsWithoutRef<typeof Skeleton>;
  main?: BoxProps;
  mainContent?: BoxProps;
  fareItem?: Pick<React.ComponentPropsWithoutRef<typeof FareTable>, 'itemSettings'>;
  loading?: React.ComponentPropsWithoutRef<typeof Loading>;
  error?: React.ComponentPropsWithoutRef<typeof Error>;
  noResults?: React.ComponentPropsWithoutRef<typeof NoResults>;
  table?: Exclude<React.ComponentPropsWithoutRef<typeof FareTable>, 'itemSettings'>;
  paginationContainer?: BoxProps;
  pagination?: React.ComponentPropsWithoutRef<typeof Pagination>;
  disclaimer?: React.ComponentPropsWithoutRef<typeof Disclaimer>;
}

type FareTableProps = StyledPropsWithChildren<Props>;

function FareTableContainer(props: FareTableProps) {
  const {
    filters = {
      availableFilters: ['OriginDestination', 'SingleDateRangePicker', 'Budget']
    },
    components = {},
    noFaresBehavior = 'hidden',
    faresTransformationLogic = 'grouped-routes',
    paginationSettings,
    initialFares,
    fareItemOnClickAction,
    prePopulation,
    integrationSettings,
    meta,
    getFares: getFaresFn,
    getAirports: getAirportsFn,
    showFilters = true,
    direction,
    theme,
    secondaryFilters,
    showSecondaryFilters = false,
    originDestinationLogic = OriginDestinationLogic.SAME,
    enableSEO = true,
    pageContext
  } = props;

  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: '600px' }, style);

  const { focusIndex, setFocusIndex, ...focusHandlers } = useListFocusHandlers();

  const {
    fares: { match, ...fares },
    retry,
    dispatch,
    tracking,
    ...filtersState
  } = useFilters({
    originDestinationLogic,
    // @ts-ignore
    filter: { prePopulation, integrationSettings, meta, pageContext, getAirports: getAirportsFn },
    initialData: initialFares,
    minInitialResults: noFaresBehavior === 'hidden' ? 1 : null,
    getFares: getFaresFn,
    paginationSettings,
    faresTransformationLogic,
    availableFilters: showFilters ? filters.availableFilters : [],
    showSecondaryFilters,
    availableSecondaryFilter: secondaryFilters ? secondaryFilters.availableFilters : [],
    hideBudgetOnMultiCurrency: filters.hideBudgetOnMultiCurrency,
    showOrigin: filters.originDestinationSettings && filters.originDestinationSettings.showOrigin,
    showDestination:
      filters.originDestinationSettings && filters.originDestinationSettings.showDestination,
    ...focusHandlers
  });

  const itemSettings = useFareItemProps(components.fareItem, dispatch, fareItemOnClickAction);
  const [nextFocusIndex, listItemRef] = useListItemFocus({
    data: fares.data,
    focusIndex,

    // @ts-ignore
    setFocusIndex
  });

  const loadMoreRef = useRef(null);

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }

  function handleSeeMoreDeals() {
    tracking({ type: 'MORE_DEALS' });
    return true;
  }

  return (
    <Box
      className={`${FareTableContainer.componentName} ${className}`}
      style={myStyle}
      position="relative"
      dataTestId="fareListContainer"
      {...components.main}
    >
      {match('SKELETON') && (
        <Skeleton
          position="absolute"
          width="100"
          {...components.skeleton}
          showFilters={showFilters}
        />
      )}
      <HeaderContainer
        defaultTitleValue={'FIND THE BEST DEALS WITH AMERICAN'}
        handleSeeMoreDeals={handleSeeMoreDeals}
        {...(props as any)}
      />
      <Box position="relative" {...components.mainContent}>
        {match('LOADING') && <Loading {...components.loading} />}
        {showFilters && <InlineFilters {...filters} {...filtersState} />}
        {showSecondaryFilters && (
          <SecondaryFilters
            {...filters}
            {...secondaryFilters}
            {...filtersState}
            availableFilters={filtersState.availableSecondaryFilters}
            filter={{ ...{ prePopulation, integrationSettings, meta, getAirports } }}
            loading={match('SKELETON')}
          />
        )}
        {match('ERROR') && (
          <Row>
            <Col>
              <Error tryAgain={retry} {...components.error} />
            </Col>
          </Row>
        )}
        {match('NO_RESULTS') && (
          <Row>
            <Col>
              <NoResults {...components.noResults} />
            </Col>
          </Row>
        )}
        {match('COMPLETE') && (
          <FareTable
            selectedItemRef={listItemRef}
            focusOn={nextFocusIndex}
            data={fares.data}
            {...components.table}
            itemSettings={itemSettings}
          />
        )}
        {fares.hasMore() && (
          <Box
            textAlign="center"
            marginTop="2"
            marginBottom="3"
            {...components.paginationContainer}
          >
            <Pagination
              isLoading={match('LOADING')}
              onClickAction={fares.loadMore}
              focusReferences={loadMoreRef}
              {...components.pagination}
            />
          </Box>
        )}
        <Row>
          <Col>
            <Disclaimer labelKey="cmp-fare-table-container-disclaimer" {...components.disclaimer} />
          </Col>
        </Row>
      </Box>
    </Box>
  );
}

FareTableContainer.defaultProps = {
  componentName: 'FareTable',
  getFares,
  getAirports,
  paginationSettings: {
    step: 10,
    limit: 30,
    initialVisibleItems: 10
  },
  prePopulation: prePopulationDefaultProps,
  titleKey: 'cmp-fare-table-title',
  titleSettings: {},
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'fareTable'
  },
  showSubTitle: false,
  subHeaderContentSettings: {},
  subTitleKey: 'cmp-fare-filterable-table-sub-title',
  subTitleSettings: {}
};

FareTableContainer.componentName = 'FareTable';

// HOC with common functionality to all containers
export default withCommonEMContainer(FareTableContainer);
